import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { format, parseISO } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Paper,
  List,
  ListItem,
  Badge,
  CircularProgress,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Image as ImageIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Search as SearchIcon,
  MarkEmailUnread as RequestsIcon,
  ChatBubbleOutline as ChatEmptyIcon,
} from '@mui/icons-material';
import { 
  HubConnectionBuilder, 
  LogLevel, 
  HttpTransportType 
} from '@microsoft/signalr';
import { ChatMessages, ChatSendFile, Chats } from "../../services/chatService";
import { API_BASE } from "../../config/env";
import { toast } from 'react-toastify';
import { decrypt } from '../../utils/crypto';
import VerifiedBadge from '../../components/common/VerifiedBadge';

const ChatScreen = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { id: chatId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connection, setConnection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [chatRequest, setChatRequests] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatPage, setChatPage] = useState(1);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const CHAT_PAGE_SIZE = 10;

  const { userName, isActive, isIdentityVerified } = location.state || {};

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'HH:mm');
    } catch {
      return '';
    }
  };

  const formatDayLabel = (dateString) => {
    if (!dateString) return '';
    try {
      const locales = { en: enUS, tr: tr };
      const locale = locales[i18n.language] || enUS;
      const date = parseISO(dateString);
      const now = new Date();
      const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startMsg = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.round((startToday - startMsg) / 86400000);
      if (diffDays === 0) return t('chat.today');
      if (diffDays === 1) return t('chat.yesterday');
      return format(date, 'd MMMM yyyy', { locale });
    } catch {
      return '';
    }
  };

  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    try {
      const da = parseISO(a);
      const db = parseISO(b);
      return (
        da.getFullYear() === db.getFullYear() &&
        da.getMonth() === db.getMonth() &&
        da.getDate() === db.getDate()
      );
    } catch {
      return false;
    }
  };

  const formatListTime = (dateString) => {
    if (!dateString) return '';
    try {
      const locales = { en: enUS, tr: tr };
      const locale = locales[i18n.language] || enUS;
      const date = parseISO(dateString);
      const now = new Date();
      const sameDay =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();
      return format(date, sameDay ? 'HH:mm' : 'd MMM', { locale });
    } catch {
      return '';
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const messageContainer = messagesEndRef.current.parentElement;
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  };

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.isNew || lastMessage?.isMe) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    let isActive = true;

    const initializeConnection = async () => {
      const token = localStorage.getItem('authToken');
      const options = {
        withCredentials: false,
        // WebSocket öncelikli; nginx/proxy sorununda LongPolling fallback
        transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
      };

      if (token) {
        const encryptedToken = decrypt(token);
        if (encryptedToken) {
          options.accessTokenFactory = () => encryptedToken;
        }
      }

      const connect = new HubConnectionBuilder()
        .withUrl(`${API_BASE}`, options)
        .configureLogging(LogLevel.Warning)
        .withAutomaticReconnect()
        .build();

      try {
        await connect.start();
        setConnection(connect);

        connect.on('ReceiveMessage', (receivedMessage) => {
          if (isActive) {
            if (receivedMessage.chatId.toString() === chatId.toString()) {
              setMessages(prev => [...prev, { ...receivedMessage, isNew: true }]);
              markAllAsRead(receivedMessage.chatId);

            }
            else {
            }
          }
        });

        connect.on('ReadMessage', (data) => {
          if (isActive) {
            if (data.id.toString() === chatId.toString()) {
              setMessages(prev =>
                prev.map(msg => ({
                  ...msg,
                  isVisible: true
                }))
              );
            }
          }
        });

        connect.onclose((error) => {
          if (isActive) {
            setTimeout(() => {
              initializeConnection();
            }, 5000);
          }
        });

        connect.onreconnecting(() => {
        //  toast.info(t('chat.connectionLost'));
        });

        connect.onreconnected(() => {
          //toast.success(t('chat.connectionRestored'));
          fetchMessages();
        });

      } catch (err) {
        console.error('Connection failed: ', err);
       // toast.error(t('chat.connectionError'));
        if (isActive) {
          setTimeout(() => {
            initializeConnection();
          }, 5000);
        }
      }
    };

    initializeConnection();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca sohbet değişince
  }, [chatId]);

  useEffect(() => {
    fetchMessages();
    fetchChatRooms();
    if (chatId) {
      markAllAsRead(chatId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca sohbet değişince
  }, [chatId]);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const response = await ChatMessages(chatId);
      if (response.statusCode === 200) {
        const messages = response.data.map(msg => ({ ...msg, isNew: false }));
        setMessages(messages);
      }
    } catch (error) {
      toast.error(t('chat.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage?.trim() || !chatId) return;

    try {
      await connection?.invoke("SendMessage", parseInt(chatId), newMessage.toString());
      setMessages(prev => [
        ...prev,
        {
          message: newMessage,
          isMe: true,
          isNew: true,
          sentAt: new Date().toISOString(),
          chatId: parseInt(chatId),
          isVisible: false
        }
      ]);
      setNewMessage('');
    } catch (error) {
      console.error(error);
      toast.error(t('chat.sendError'));
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("message", newMessage);
    formData.append("chatid", chatId);
    formData.append("file", file);

    try {
      const response = await ChatSendFile(formData);
      if (response.statusCode === 200) {
        await connection.invoke("SendImageMessage", response.data.id);
        setMessages(prev => [...prev, response.data]);
        setNewMessage('');
      }
    } catch (error) {
      toast.error(t('chat.fileError'));
    }
  };

  const markAllAsRead = async (messageId) => {
    if (connection) {
      try {
        await connection.invoke('MarkAllAsRead', parseInt(messageId || chatId));
      } catch (error) {
      }
    }
    else {
    }
  };

  const fetchChatRooms = React.useCallback(async (page = 1, append = false) => {
    try {
      if (append) setChatLoadingMore(true);
      const response = await Chats({ pageIndex: page, pageSize: CHAT_PAGE_SIZE });
      if (response.statusCode === 200) {
        const payload = response.data;
        const items = payload?.items ?? payload?.Items ?? (Array.isArray(payload) ? payload : []);
        const hasNext = payload?.hasNextPage ?? payload?.HasNextPage
          ?? ((payload?.pageIndex ?? payload?.PageIndex ?? page) < (payload?.totalPages ?? payload?.TotalPages ?? 1));
        setChatRooms((prev) => (append ? [...prev, ...items] : items));
        setChatPage(page);
        setChatHasMore(Boolean(hasNext));
        setChatRequests(response.count || 0);
      }
    } catch (error) {
      toast.error(t('chat.error'));
    } finally {
      setChatLoadingMore(false);
    }
  }, [t]);

  const loadMoreChats = () => {
    if (!chatHasMore || chatLoadingMore) return;
    fetchChatRooms(chatPage + 1, true);
  };

  const filteredChatRooms = chatRooms.filter(room =>
    room?.atUserName?.toString().toLowerCase().includes(searchQuery?.toLowerCase() || '') || false
  );

  return (
    <Box
      sx={{
        height: { xs: 'calc(100vh - 70px)', md: 'calc(100vh - 80px)' },
        display: 'flex',
        gap: { xs: 0, md: 2 },
        p: { xs: 0, md: 2 },
        maxWidth: 1400,
        mx: 'auto',
        bgcolor: 'background.default',
      }}
    >
      {/* Sol: sohbet listesi */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: chatId ? 0 : '100%', md: 360 },
          display: { xs: chatId ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column',
          borderRadius: { xs: 0, md: 3 },
          overflow: 'hidden',
          border: (t) => ({ xs: 'none', md: `1px solid ${t.palette.divider}` }),
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            background: (t) =>
              `linear-gradient(135deg, ${alpha(t.palette.primary.main, isDark ? 0.18 : 0.08)} 0%, transparent 70%)`,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {t('chat.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('chat.subtitle')}
            </Typography>
          </Box>
          <IconButton
            onClick={() => navigate('/chat/requests')}
            aria-label={t('chat.requests')}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
            }}
          >
            <Badge badgeContent={chatRequest} color="error">
              <RequestsIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('chat.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: isDark ? alpha('#fff', 0.04) : alpha(theme.palette.grey[500], 0.08),
              },
            }}
          />
        </Box>

        <Box
          sx={{ flex: 1, overflow: 'auto' }}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
              loadMoreChats();
            }
          }}
        >
          {!filteredChatRooms?.length ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  mb: 0.5,
                }}
              >
                <ChatEmptyIcon />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {t('chat.noChats')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {searchQuery ? t('chat.search') : t('chat.startNewChat')}
              </Typography>
            </Box>
          ) : (
          <List sx={{ p: 0 }}>
            {filteredChatRooms?.map((room) => {
              const selected = chatId === room.id?.toString();
              const name = room.atUserName || room.toUserName;
              const online = Boolean(room.atUserIsActive || room.toUserIsActive);
              return (
                <ListItem
                  key={room.id}
                  button
                  selected={selected}
                  onClick={() =>
                    navigate(`/chat/${room.id}`, {
                      state: {
                        userName: name,
                        isActive: online,
                        isIdentityVerified: room.atUserIsIdentityVerified === true,
                      },
                    })
                  }
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    transition: 'background-color 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12),
                      },
                    },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{
                        '& .MuiBadge-dot': {
                          bgcolor: online ? 'success.main' : 'transparent',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          border: online ? `2px solid ${theme.palette.background.paper}` : 'none',
                        },
                      }}
                    >
                      <Avatar
                        src={room.atUserPhoto || room.toUserPhoto || ''}
                        alt={name || ''}
                        sx={{
                          width: 42,
                          height: 42,
                          border: `2px solid ${alpha(theme.palette.primary.main, selected ? 0.35 : 0.12)}`,
                        }}
                        onError={(e) => {
                          e.target.src = '/default-avatar.png';
                        }}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={room.newActive ? 700 : 600}
                          noWrap
                          sx={{ maxWidth: 140 }}
                        >
                          {name}
                        </Typography>
                        <VerifiedBadge verified={room.atUserIsIdentityVerified} size={14} focusable={false} sx={{ mr: 'auto' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatListTime(room.lastAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 200,
                            fontSize: '0.8rem',
                            fontWeight: room.newActive ? 600 : 400,
                          }}
                        >
                          {room.lastMessage || ''}
                        </Typography>
                        {room.newActive && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              ml: 'auto',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
            {chatLoadingMore && (
              <Box display="flex" justifyContent="center" py={1.5}>
                <CircularProgress size={22} />
              </Box>
            )}
          </List>
          )}
        </Box>
      </Paper>

      {/* Sağ: konuşma */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: { xs: chatId ? 'flex' : 'none', md: 'flex' },
          flexDirection: 'column',
          borderRadius: { xs: 0, md: 3 },
          overflow: 'hidden',
          border: (t) => ({ xs: 'none', md: `1px solid ${t.palette.divider}` }),
          bgcolor: 'background.paper',
          minWidth: 0,
        }}
      >
        {chatId ? (
          <>
            <Box
              sx={{
                px: { xs: 1.5, md: 2.5 },
                py: 1.75,
                display: 'flex',
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: isDark ? alpha('#fff', 0.02) : alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <IconButton
                onClick={() => navigate('/chat')}
                sx={{ mr: 1, display: { xs: 'flex', md: 'none' } }}
                aria-label={t('common.back')}
              >
                <ArrowBackIcon />
              </IconButton>

              <Avatar
                sx={{
                  mr: 1.5,
                  width: 44,
                  height: 44,
                  border: isActive
                    ? `2px solid ${theme.palette.success.main}`
                    : `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
                alt={userName}
                onError={(e) => {
                  e.target.src = '/default-avatar.png';
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {userName}
                  </Typography>
                  <VerifiedBadge verified={isIdentityVerified === true} size={17} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: isActive ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {isActive ? t('chat.online') : t('chat.offline')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                px: { xs: 1.5, md: 3 },
                py: 2.5,
                bgcolor: isDark ? alpha('#000', 0.25) : alpha(theme.palette.grey[500], 0.06),
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha(theme.palette.text.primary, 0.18),
                  borderRadius: 3,
                },
              }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                messages.map((message, index) => {
                  const showDay =
                    index === 0 || !isSameDay(messages[index - 1]?.sentAt, message.sentAt);
                  return (
                    <React.Fragment key={`${message.id}-${message.sentAt}-${index}`}>
                      {showDay && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.75 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              px: 1.25,
                              py: 0.4,
                              borderRadius: 999,
                              fontWeight: 600,
                              bgcolor: isDark
                                ? alpha('#fff', 0.08)
                                : alpha(theme.palette.grey[700], 0.1),
                              color: 'text.secondary',
                            }}
                          >
                            {formatDayLabel(message.sentAt)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: message.isMe ? 'flex-end' : 'flex-start',
                          mb: 0.85,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: { xs: '82%', md: '62%' },
                            bgcolor: message.isMe
                              ? theme.palette.primary.main
                              : isDark
                                ? alpha('#fff', 0.07)
                                : '#fff',
                            color: message.isMe ? '#fff' : 'text.primary',
                            borderRadius: message.isMe
                              ? '14px 14px 4px 14px'
                              : '14px 14px 14px 4px',
                            px: 1.4,
                            py: 0.9,
                            border: message.isMe
                              ? 'none'
                              : `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                            boxShadow: message.isMe
                              ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.22)}`
                              : `0 1px 4px ${alpha('#0F172A', 0.05)}`,
                          }}
                        >
                          {message.isFile && message.file ? (
                            <Box
                              component="img"
                              src={message.file}
                              alt={t('chat.sharedFile')}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/broken-image.png';
                              }}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 240,
                                objectFit: 'contain',
                                borderRadius: 1.25,
                                mb: 0.75,
                                cursor: 'pointer',
                                display: 'block',
                              }}
                              onClick={() => window.open(message.file, '_blank')}
                            />
                          ) : null}
                          {message.message ? (
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45, fontSize: '0.9rem' }}
                            >
                              {message.message}
                            </Typography>
                          ) : null}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              mt: 0.35,
                              gap: 0.35,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: message.isMe ? 0.8 : 0.6,
                                fontSize: '0.65rem',
                              }}
                            >
                              {formatMessageTime(message.sentAt)}
                            </Typography>
                            {message.isMe &&
                              (message.isVisible ? (
                                <DoneAllIcon sx={{ fontSize: 13, opacity: 0.9 }} />
                              ) : (
                                <DoneIcon sx={{ fontSize: 12, opacity: 0.75 }} />
                              ))}
                          </Box>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            <Box
              sx={{
                p: { xs: 1, md: 1.25 },
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'background.paper',
              }}
            >
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                aria-label={t('chat.attachImage')}
                size="small"
                sx={{
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                }}
              >
                <ImageIcon fontSize="small" />
              </IconButton>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('chat.writeMessage')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    bgcolor: isDark ? alpha('#fff', 0.04) : alpha(theme.palette.grey[500], 0.06),
                    fontSize: '0.9rem',
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                aria-label={t('chat.send')}
                sx={{
                  bgcolor: 'primary.main',
                  color: '#fff',
                  width: 38,
                  height: 38,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: alpha(theme.palette.primary.main, 0.25),
                    color: alpha('#fff', 0.6),
                  },
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.5,
              px: 3,
              background: (t) =>
                `radial-gradient(circle at 30% 20%, ${alpha(t.palette.primary.main, 0.08)}, transparent 45%),
                 radial-gradient(circle at 70% 80%, ${alpha(t.palette.primary.main, 0.05)}, transparent 40%)`,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 0.5,
              }}
            >
              <ChatEmptyIcon sx={{ fontSize: 34 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} textAlign="center">
              {t('chat.selectChat')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={360}>
              {t('chat.startNewChat')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ChatScreen; 